import React, { useState, useEffect } from 'react';
import { Table, Input, Typography, Card, Button } from 'antd';
import { SearchOutlined, EyeOutlined } from '@ant-design/icons';
import { Resizable } from 'react-resizable';
import CurrencyDetailDrawer from './CurrencyDetailDrawer';
import { getCurrencies } from '../services/api';
import { getCookie, setCookie } from '../utils/cookies';
import 'react-resizable/css/styles.css';

const { Title, Text } = Typography;

const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 10,
            cursor: 'col-resize',
            zIndex: 1,
          }}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const CurrencyList = () => {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: parseInt(getCookie('currencyPageSize'), 10) || 10,
    total: 0,
  });
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [columnWidths, setColumnWidths] = useState({
    code: 90,
    name: 280,
    symbol: 80,
    actions: 88,
  });
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    fetchCurrencies(pagination.current, pagination.pageSize, appliedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, appliedSearch]);

  const fetchCurrencies = async (page, pageSize, search) => {
    try {
      setLoading(true);
      setFetchError(null);
      const data = await getCurrencies(page, pageSize, search);
      const rows = Array.isArray(data) ? data : data.results || [];
      setCurrencies(rows);
      setPagination((prev) => ({
        ...prev,
        total: Array.isArray(data) ? rows.length : data.count || 0,
        current: page,
        pageSize,
      }));
    } catch (error) {
      console.error('Error fetching currencies:', error);
      setFetchError('Failed to load currencies. Please try again.');
      setCurrencies([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (newPagination) => {
    if (newPagination.pageSize !== pagination.pageSize) {
      setCookie('currencyPageSize', newPagination.pageSize);
      newPagination.current = 1;
    }
    setPagination(newPagination);
  };

  const handleSearch = (value) => {
    setAppliedSearch((value || '').trim());
    setPagination((prev) => ({ ...prev, current: 1 }));
  };

  const handleResize =
    (index) =>
    (_, { size }) => {
      const colKeys = ['code', 'name', 'symbol', 'actions'];
      const key = colKeys[index];
      if (key) {
        setColumnWidths((prev) => ({ ...prev, [key]: size.width }));
      }
    };

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: columnWidths.code,
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: columnWidths.name,
    },
    {
      title: 'Symbol',
      dataIndex: 'symbol',
      key: 'symbol',
      width: columnWidths.symbol,
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: columnWidths.actions,
      render: (_, record) => (
        <Button
          type="link"
          size="small"
          icon={<EyeOutlined />}
          onClick={() => {
            setSelectedCurrency(record);
            setDrawerOpen(true);
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const resizableColumns = columns.map((col, index) => ({
    ...col,
    onHeaderCell: (column) => ({
      width: column.width,
      onResize: handleResize(index),
    }),
  }));

  return (
    <div className="app-table-page">
      <Card>
        <Title level={4}>Currencies</Title>
        <Input.Search
          placeholder="Search code or name"
          allowClear
          enterButton={<SearchOutlined />}
          style={{ maxWidth: 360, marginBottom: 16 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onSearch={handleSearch}
        />
        {fetchError && (
          <Typography.Text type="danger" style={{ display: 'block', marginBottom: 12 }}>
            {fetchError}
          </Typography.Text>
        )}
        <Typography.Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 12 }}>
          FX rates load when you open a currency.
        </Typography.Text>
        <Table
          columns={resizableColumns}
          dataSource={currencies}
          rowKey="id"
          loading={loading}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `Total ${total} currencies`,
          }}
          onChange={handleTableChange}
          scroll={{ x: true, y: 'calc(100vh - 300px)' }}
          sticky={{ offsetHeader: 0 }}
          style={{ marginBottom: 16 }}
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
        />
        <CurrencyDetailDrawer
          currencyId={selectedCurrency?.id}
          initialCurrency={selectedCurrency}
          open={drawerOpen}
          onClose={() => {
            setDrawerOpen(false);
            setSelectedCurrency(null);
          }}
        />
      </Card>
    </div>
  );
};

export default CurrencyList;
